import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import messageSound from "@/assets/sounds/message-audio.mp3";
import { Badge } from "@/components/ui/badge";
import { MESSAGES_CONSTS, MESSAGE_COUNT } from "@/constants/message.constant";
import {
  TSidebarItem,
  businessSidebarData,
} from "@/constants/routes_sidebar/SidebarLinks";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import { useSocket } from "@/hooks/useSocket";
import { isSubDomain } from "@/lib/utils";

type useMessageCounterProps = {
  getMessageCount: () => number;
  setMessageCount: (count: number) => void;
  updatedBusinessRoutes: TSidebarItem[];
};

const useMessageCounter = (): useMessageCounterProps => {
  const { pathname } = useLocation();
  const { socket } = useSocket("/");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isListenerActive = useRef(false);

  // Add state to track message count
  const [messageCount, setMessageCountState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(MESSAGE_COUNT) || "0");
    } catch {
      return 0;
    }
  });

  // Initialize audio element only once
  useEffect(() => {
    audioRef.current = new Audio(messageSound);
    audioRef.current.preload = "auto"; // Preload the audio

    return (): void => {
      // Clean up audio element when component unmounts
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const getMessageCount = useCallback((): number => {
    try {
      return JSON.parse(localStorage.getItem(MESSAGE_COUNT) || "0");
    } catch {
      return 0;
    }
  }, []);

  const setMessageCount = useCallback(
    (count: number): void => {
      if (pathname.includes(ROUTES.MESSAGES)) {
        return;
      }

      localStorage.setItem(MESSAGE_COUNT, JSON.stringify(count));
      setMessageCountState(count); // Update state when count changes
    },
    [pathname],
  );

  const resetCount = useCallback((): void => {
    localStorage.setItem(MESSAGE_COUNT, "0");
    setMessageCountState(0);
  }, []);

  const messageHandler = useCallback(async (): Promise<void> => {
    if (pathname.includes(ROUTES.MESSAGES) || !isSubDomain) {
      return;
    }

    if (
      audioRef.current &&
      audioRef.current.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA
    ) {
      try {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Audio playback failed:", e);
      }
    }

    setMessageCountState((prevCount: number) => {
      const newCount = prevCount + 1;
      localStorage.setItem(MESSAGE_COUNT, JSON.stringify(newCount));

      return newCount;
    });
  }, [pathname]);

  const updatedBusinessRoutes = useMemo(() => {
    return businessSidebarData.map((item) =>
      item.text === "Message" && messageCount > 0
        ? {
            ...item,
            alert: (
              <Badge variant={"light"} className='px-1 py-1 rounded-full'>
                {messageCount}
              </Badge>
            ),
          }
        : item,
    );
  }, [messageCount]);

  // Reset count when navigating to messages page
  useEffect(() => {
    if (pathname.includes(ROUTES.MESSAGES)) {
      resetCount();
    }
  }, [pathname, resetCount]);

  // Setup socket listener with cleanup
  useEffect(() => {
    // Skip if socket not available or listener is already active
    if (!socket || isListenerActive.current) {
      return;
    }

    // Add event listener and mark as active
    socket.on(MESSAGES_CONSTS.CONSTS.MESSAGE, messageHandler);
    isListenerActive.current = true;

    // Clean up function to remove listener when component unmounts
    // eslint-disable-next-line consistent-return
    return (): void => {
      socket.off(MESSAGES_CONSTS.CONSTS.MESSAGE, messageHandler);
      isListenerActive.current = false;
    };
  }, [socket, messageHandler]);

  return {
    getMessageCount,
    setMessageCount,
    updatedBusinessRoutes,
  };
};

export default useMessageCounter;
